/**
 * `/v1/sectors/:id` in local mode (plan §6.6).
 *
 * Panel sizes live in `constants/panels` with the rest of the closed product
 * roster — presentation shortlists, not generator parameters.
 */
import {
  SECTOR_IDS,
  type SectorDetail,
  type SectorId,
  type SectorQuery,
} from '@meridian/contracts';

import { buildCorrelationMatrix } from '../../analytics/correlation-matrix';
import { aggregateSectors, toSectorAggregate } from '../../analytics/sector-aggregate';
import { sectorDiversifiers } from '../../analytics/sector-diversifiers';
import { SECTOR_DIVERSIFIER_PANEL } from '../../constants/diversification';
import { MATRIX_SIZE, MOVERS_SIZE } from '../../constants/panels';
import { SECN } from '../../constants/sectors';
import { DatasetNotFoundError } from '../../ports/dataset-repository';
import { filterUniverse } from '../../selectors/filter-universe';
import { topByReturn } from '../../selectors/rankings';
import { sortRows } from '../../selectors/sort-rows';
import { toWireSecurity, type DerivedSecurity } from '../../universe/derive-security';

function isSectorId(value: string): value is SectorId {
  return (SECTOR_IDS as readonly string[]).includes(value);
}

export function buildSectorDetail(
  rows: readonly DerivedSecurity[],
  id: SectorId,
  query: SectorQuery,
): SectorDetail {
  if (!isSectorId(id)) throw new DatasetNotFoundError('sector', id);
  const pool = filterUniverse(rows, query.index);
  const members = pool.filter((r) => r.sc === id);
  /*
   * An index filter that empties a real sector is a 404, not an empty 200. The
   * alternative would be a SectorDetail whose `matrix.tickers` is empty, which
   * CorrelationMatrixSchema rejects (min 1) — so the choice is between failing
   * here with a locatable message and failing at serialisation with a Zod error
   * naming a field the reader never set.
   */
  if (members.length === 0) {
    throw new DatasetNotFoundError('sector', `${id} (index=${query.index})`);
  }
  const [aggregate] = aggregateSectors(members, query.window);
  /* Unreachable while `members` is non-empty, but asserting it beats a non-null
     assertion: if the grouping rule ever changes, this fails where the cause is
     rather than emitting a SectorDetail with an undefined aggregate. */
  if (aggregate === undefined) {
    throw new DatasetNotFoundError('sector', `${id} (no aggregate)`);
  }
  const byMcap = sortRows(members, { k: 'mcap', d: -1 }, { window: query.window });
  return {
    sector: { id, name: SECN[id] },
    aggregate: toSectorAggregate(aggregate),
    members: members.map(toWireSecurity),
    /* `topByReturn`, not `sortRows(...).slice(...)`: sortRows pushes null-return
       rows to the tail but still slices them into the top N when fewer than N
       members have a return, handing a rank to rows never ordered by the metric.
       One definition of "top N by return" for the wire field and every client. */
    movers: topByReturn(members, query.window, MOVERS_SIZE).map(toWireSecurity),
    matrix: buildCorrelationMatrix(byMcap.slice(0, MATRIX_SIZE), query.window),
    /* Candidates are other-sector names from the WHOLE universe, not the
       index-filtered pool (demo:993): a diversifier is useful whether or not it
       sits in the index filter, but same-sector names are never diversifiers of
       their own sector (F11a: t.sc !== s.sc). */
    diversifiers: [
      ...sectorDiversifiers(
        members,
        rows.filter((r) => r.sc !== id),
        SECTOR_DIVERSIFIER_PANEL,
      ),
    ],
  };
}
